import React, { useState, useEffect } from "react";
import { Modal, Button, message, Spin } from "antd";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import i18n from "../../lang/i18n";
import {
    getCustomerWorkLogs,
    createCustomerWorkLog,
    WorkLogItem,
} from "../../api/CustomerWorkLog";

interface Props {
    open: boolean;
    onClose: () => void;
    customerId: number | null;
}

/** TinyMCE để lại <p></p>/&nbsp; khi ô trống nên không thể chỉ trim chuỗi */
const isEmptyHtml = (html: string) =>
    !html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim();

const WorkHistoryModal: React.FC<Props> = ({ open, onClose, customerId }) => {
    const { t } = useTranslation();
    const [content, setContent] = useState("");
    const [history, setHistory] = useState<WorkLogItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && customerId) {
            fetchLogs();
        } else {
            setHistory([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, customerId]);

    const fetchLogs = async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const res = await getCustomerWorkLogs(customerId);
            if (res.status && res.data?.items) {
                setHistory(res.data.items);
            }
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (isEmptyHtml(content)) return;
        if (!customerId) return;
        setSubmitting(true);
        try {
            const res = await createCustomerWorkLog(customerId, content.trim());
            if (res.status && res.data) {
                setHistory([res.data, ...history]);
                setContent("");
            } else {
                message.error(t('customer.add_failed') || "Thêm thất bại");
            }
        } catch {
            message.error(t('customer.add_failed') || "Thêm thất bại");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={t('customer.add_work_history')}
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            width={600}
            className="
        dark:[&_.ant-modal-content]:bg-[#1f1f1f]
        dark:[&_.ant-modal-header]:bg-[#1f1f1f]
        dark:[&_.ant-modal-title]:text-white
        dark:[&_.ant-modal-close]:text-gray-300
      "
        >
            {/* HISTORY LIST */}
            <div className="mb-4">
                <div className="border rounded-md overflow-hidden dark:border-[#303030]">
                    <div className="px-4 py-2 border-b dark:border-[#303030] font-medium t-text-white">
                        {t('customer.work_history')} ({history.length})
                    </div>

                    <div className="max-h-[220px] overflow-y-auto">
                        {loading ? (
                            <div className="flex justify-center py-6">
                                <Spin />
                            </div>
                        ) : history.length === 0 ? (
                            <div className="px-4 py-4 text-center text-gray-400 text-sm">
                                {t('customer.no_work_history')}
                            </div>
                        ) : (
                            history.map((item) => {
                                const creatorName = item.creator?.fullname || "";
                                const lang = i18n.language === "vi" ? "vi-VN" : "en-US";
                                const timeStr = item.created_at
                                    ? new Date(item.created_at).toLocaleString(lang)
                                    : "";
                                return (
                                    <div
                                        key={item.id}
                                        className="px-4 py-3 border-b last:border-none dark:border-[#303030]"
                                    >
                                        <div className="flex justify-between text-sm">
                                            <div className="font-semibold t-text-white">
                                                • {creatorName}
                                            </div>
                                            <div className="text-gray-500 text-xs">
                                                {timeStr}
                                            </div>
                                        </div>
                                        {/* Nội dung lưu dạng HTML (soạn bằng TinyMCE) nên phải
                                            render, để nguyên chuỗi sẽ lộ thẻ <p>, <br>, &aacute;... */}
                                        <div
                                            className="
                                                text-gray-600 text-000000e0 text-sm mt-1
                                                [&_p]:m-0 [&_ul]:m-0 [&_ul]:pl-5 [&_ol]:m-0 [&_ol]:pl-5
                                                [&_img]:max-w-full [&_img]:h-auto
                                                break-words
                                            "
                                            dangerouslySetInnerHTML={{ __html: item.content || "" }}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* INPUT */}
            <div className="mb-4">
                <label className="text-sm font-medium t-text-white">
                    <span className="text-red-500">*</span> {t('customer.content')}
                </label>

                <div className="mt-2">
                    <Editor
                        // Bản self-host (GPL) trong public/tinymce — không cần API key cloud
                        tinymceScriptSrc={`${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`}
                        licenseKey="gpl"
                        value={content}
                        onEditorChange={setContent}
                        init={{
                            height: 220,
                            menubar: false,
                            branding: false,
                            resize: false,
                            skin: document.documentElement.classList.contains("dark") ? "oxide-dark" : "oxide",
                            content_css: document.documentElement.classList.contains("dark") ? "dark" : "default",
                            plugins: ["link", "lists", "table", "code", "fullscreen", "preview"],
                            toolbar:
                                "undo redo | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link table | preview fullscreen code",
                            placeholder: t('customer.enter_work_detail'),
                            paste_data_images: false,
                        }}
                    />
                </div>
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-3">
                <Button
                    onClick={onClose}
                    className="dark:bg-[#2a2a2a] dark:border-[#303030] text-000000e0"
                >
                    {t('customer.cancel')}
                </Button>

                <Button
                    type="primary"
                    onClick={handleAdd}
                    loading={submitting}
                    disabled={isEmptyHtml(content)}
                    className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                >
                    {t('customer.add')}
                </Button>
            </div>
        </Modal>
    );
};

export default WorkHistoryModal;